import React, { useEffect } from "react";
import styles from "./HomeFinancialFeasibility.scss";
import cx from "../../../utils/cx";
import RedIconActive from '@mui/icons-material/CancelRounded';
import RedIcon from '@mui/icons-material/CancelOutlined';
import YellowIconActive from '@mui/icons-material/RemoveCircleRounded';
import YellowIcon from '@mui/icons-material/RemoveCircleOutlineRounded';
import GreenIconActive from '@mui/icons-material/CheckCircleRounded';
import GreenIconIcon from '@mui/icons-material/CheckCircleOutlineRounded';
import DialogTrigger from "../../MaterialUi/DialogTrigger";
import Icon from "../../MaterialUi/Icon";
import { SvgIconProps, Tooltip as MuiTooltip, IconButton } from "@mui/material";
import { confirm } from "../../AsyncModal";
import JsonApiRequest, { IFinancialFeasibility, IFinancialFeasibilityAttributes, IFinancialFeasibilityPatch, IProperty, IUser } from "../../../utils/JsonApiRequest";
import PropertyRequest from "../../../utils/PropertyRequest";
import createToast from "../../MaterialUi/Toast";
import LogError from "../../../utils/LogError";


export interface IHomeFinancialFeasibilityProps {
    className?: string;
    advisorUuid: IUser['id']; // User-UUID des Bankberaters
    customerUuid: IUser['id']; // User-UUID des Kunden, der die Immobilie beim Makler angefragt hat
    propertyId: IProperty['drupal_internal__id']; // ID der Immobilie
    source: IFinancialFeasibilityAttributes['source'];
    onSubmitted?: (status?: IFinancialFeasibility['status']) => void;
}

export const FinancialFeasibilitySource = {
    'realEstateRequest': 'Angefragtes Objekt',
    'favourite': 'Merklisteneintrag',
    'savedSearch': 'Suchauftragstreffer',
}

const createFinancialFeasibility = (advisorUuid: IUser['id'], customerUuid: IUser['id'], propertyId: IProperty['drupal_internal__id'], status: IFinancialFeasibility['status'], source: IFinancialFeasibility['source']) => {

    // first we need to get the property uuid
    return PropertyRequest.getPropertyUuidByDrupalId(propertyId).then((propertyUuid) => {
        if (propertyUuid) {
            const data: IFinancialFeasibilityPatch = {
                type: "financial_feasibility--financial_feasibility",
                id: undefined,
                attributes: {
                    status,
                    source
                },
                relationships: {
                    uid: {
                        data: { type: "user--user", id: advisorUuid }
                    },
                    customer: {
                        data: { type: "user--user", id: customerUuid }
                    },
                    real_estate: {
                        data: { type: "real_estate_property--real_estate_property", id: propertyUuid }
                    }
                }

            }
            return JsonApiRequest.postRequest('/financial_feasibility/financial_feasibility', { data: data });
        } else {
            throw new Error("Property not found");
        }
    }).catch((e) => {
        console.error(e);
        LogError.add(e, {
            title: 'Fehler beim Laden der Immobilie',
            error_code: 'HomeFinancialFeasibility:createFinancialFeasibility:getPropertyUuidByDrupalId',
            request_data: { advisorUuid, customerUuid, propertyId, status }
        });
    });
}

const updateFinancialFeasibility = (feasibilityId: string, status: 'red' | 'yellow' | 'green') => {
    const data: IFinancialFeasibilityPatch = {
        type: "financial_feasibility--financial_feasibility",
        id: feasibilityId,
        attributes: {
            status,
        }

    }
    return JsonApiRequest.patchRequest(`/financial_feasibility/financial_feasibility/${feasibilityId}`, { data });
}

/**
 * Ampellogik für re-properties
 */
const HomeFinancialFeasibility: React.FC<IHomeFinancialFeasibilityProps> = ({ className = '', advisorUuid, customerUuid, propertyId, source, onSubmitted }) => {

    const [isTooltipOpen, setIsTooltipOpen] = React.useState(false);
    const [activeLight, setActiveLight] = React.useState<'red' | 'yellow' | 'green'>(null);
    const [feasibilityId, setFeasibilityId] = React.useState<string | null>(null);

    const isAdvisorView = advisorUuid;

    if (!customerUuid) {
        return null;
    }

    useEffect(() => {
        // fetch the current status from the backend
        const filter = (isAdvisorView ? `filter[uid][path]=uid.id&filter[uid][value]=${advisorUuid}&filter[uid][operator]=%3D` : '')
            + `&filter[customer][path]=customer.id&filter[customer][value]=${customerUuid}&filter[customer][operator]=%3D`
            + `&filter[re][path]=real_estate.meta.drupal_internal__target_id&filter[re][value]=${propertyId}&filter[re][operator]=%3D`
        JsonApiRequest.getRequest(`/financial_feasibility/financial_feasibility?sort=-created&${filter}`).then((response) => {
            const data = JsonApiRequest.data2Object<IFinancialFeasibility>(response.getData());
            setActiveLight(data?.[0]?.status);
            setFeasibilityId(data?.[0]?.id);
        }).catch((e) => {
            console.error(e);
        });
    }, [advisorUuid, customerUuid, propertyId]);

    const saveLight = (light: IFinancialFeasibilityAttributes['status']) => {
        const oldActiveLight = activeLight;
        setActiveLight(light);
        if (feasibilityId) {
            updateFinancialFeasibility(feasibilityId, light).then(() => {
                createToast("Bewertung gespeichert", "success");
                if (onSubmitted) {
                    onSubmitted(light);
                }
            }).catch((e) => {
                console.error(e);
                setActiveLight(oldActiveLight);
                createToast("Fehler beim Speichern der Bewertung", "error");
                LogError.add(e, {
                    title: 'Fehler beim Speichern der Bewertung',
                    error_code: 'HomeFinancialFeasibility:saveLight',
                    request_data: { advisorUuid, customerUuid, propertyId, light }
                });
            });
        } else {
            createFinancialFeasibility(advisorUuid, customerUuid, propertyId, light, source).then(() => {
                createToast("Bewertung gespeichert", "success");
                if (onSubmitted) {
                    onSubmitted(light);
                }
            }).catch((e) => {
                console.error(e);
                setActiveLight(oldActiveLight);
                createToast("Fehler beim Anlegen der Bewertung", "error");
                LogError.add(e, {
                    title: 'Fehler beim Anlegen der Bewertung',
                    error_code: 'HomeFinancialFeasibility:saveLight:new',
                    request_data: { advisorUuid, customerUuid, propertyId, light }
                });
            });
        }
    };


    const handleSetActiveLight = (light: 'red' | 'yellow' | 'green') => () => {
        if (activeLight) {
            if (activeLight === light) {
                confirm(<>Willst du diese Bewertung löschen?</>, { title: "Bewertung löschen" }).then(() => {
                    saveLight(null);
                }).catch(() => {
                    // closed modal
                });
            } else {
                confirm(<div className={styles.homeFinancialFeasibility}>Willst du diese Bewertung auf <HomeFinancialFeasibilityIcon name={light} active /> ändern?</div>, { title: "Bewertung ändern" }).then(() => {
                    saveLight(light);
                }).catch(() => {
                    // closed modal
                });
            }
        } else {
            saveLight(light);
        }
    };

    return <div className={cx(styles.homeFinancialFeasibility, className, isAdvisorView ? styles.advisor : '' )}>

        {isAdvisorView ? (<>
            <HomeFinancialFeasibilityIcon name="red" active={activeLight ? activeLight === 'red' : true} onClick={handleSetActiveLight('red')} /> 
            <HomeFinancialFeasibilityIcon name="yellow" active={activeLight ? activeLight === 'yellow' : true} onClick={handleSetActiveLight('yellow')} /> 
            <HomeFinancialFeasibilityIcon name="green" active={activeLight ? activeLight === 'green' : true} onClick={handleSetActiveLight('green')} /> 
        </>) : (
            <HomeFinancialFeasibilityIcon name={activeLight} active={true} /> 
        )}

        {isAdvisorView && (
            <DialogTrigger
                title="Anzeige der Finanzierbarkeit"
                trigger={<IconButton  size="small"  ><Icon name="infoOutlined" sx={{width: 15, height: 15}} /></IconButton>}
                open={isTooltipOpen}
                onClick={() => { setIsTooltipOpen(true) }}
                onClose={() => { setIsTooltipOpen(false) }}
                maxWidth="md"
            >
                <TooltipText />
            </DialogTrigger>
        )}

    </div >
};

export const HomeFinancialFeasibilityIcon: React.FC<{ active?: boolean, name: 'red' | 'yellow' | 'green' } & SvgIconProps> = ({ active = true, name, ...rest }) => {

    switch (name) {
        case 'red':
            return <MuiTooltip arrow title="Eine Finanzierung der Immobilie ist mit den aktuell vorliegenden Informationen voraussichtlich nicht möglich.">
                {active ? <RedIconActive className={styles[name]} {...rest} /> : <RedIcon className={styles[name]}  {...rest} />}
            </MuiTooltip>;
        case 'yellow':
            return <MuiTooltip arrow title="Eine Finanzierung der Immobilie ist vorbehaltlich einer positiven Kreditbewilligung eventuell möglich.">
                {active ? <YellowIconActive className={styles[name]}  {...rest} /> : <YellowIcon className={styles[name]}  {...rest} />}
            </MuiTooltip>;
        case 'green':
            return <MuiTooltip arrow title="Eine Finanzierung der Immobilie ist vorbehaltlich einer positiven Kreditbewilligung wahrscheinlich möglich.">
                {active ? <GreenIconActive className={styles[name]} {...rest} /> : <GreenIconIcon className={styles[name]}  {...rest} />}
            </MuiTooltip>;
        default:
            return null;
    }
}

const TooltipText = () => {
    return <div className={styles.homeFinancialFeasibilityTooltip}>
        <h3><HomeFinancialFeasibilityIcon name="green" /> Grün</h3>
        <p>
            Eine grüne Ampel ist eine Indikation für eine erfolgreiche Vorprüfung der Kaufkraft deiner Kundin/ deines Kunden auf Basis der von ihr/ihm gemachten Angaben.
            Eine Finanzierung der Immobilie ist vorbehaltlich einer positiven Kreditbewilligung wahrscheinlich möglich.
        </p>
        <p>
            Diese Indikation verliert zwei Wochen nach Ausstellungsdatum seine Gültigkeit. Anschließend muss eine neue Indikation angefordert werden.
            Diese Indikation stellt keine Finanzierungszusage dar und ersetzt weder eine Finanzierungsberatung, noch eine weitergehende Bonitätsprüfung und Immobilienbewertung.
            Sie begründet keinen Rechtsanspruch auf Abschluss eines Darlehensvertrages oder eine Darlehensauszahlung.
        </p>
        <p>
            Auf Basis der von deiner Kundin/deinem Kunden gemachten Angaben und Annahmen war die Finanzierungsvorprüfung durch dich als Berater:in erfolgreich.
            Die monatliche Rate und die sich daraus ergebende finanzielle Belastung zu aktuell marktüblichen Zinsen würde bei einer
            beantragten Finanzierung - vorbehaltlich einer Prüfung von Detailnachweisen - wahrscheinlich positiv bewertet werden.
            Sofern sich nach einer Bonitätsprüfung und einer Objektbewertung der Bank oder aufgrund sonstiger die Finanzierung betreffender Informationen keine Ablehnungsgründe
            ergeben, könnte das Darlehen vergeben werden.
        </p>

        <h3><HomeFinancialFeasibilityIcon name="yellow" /> Gelb</h3>
        <p>
            Eine gelbe Ampel ist eine Indikation für eine erfolgreiche Vorprüfung der Kaufkraft deiner Kundin/ deines Kunden auf Basis der von ihr/ihm gemachten Angaben
            unter Vorbehalt (z.B. weil noch entscheidende Informationen fehlen und mit Annahmen versehen wurden). Eine Finanzierung der Immobilie ist vorbehaltlich einer
            positiven Kreditbewilligung eventuell möglich.
        </p>
        <p>
            Diese Indikation verliert zwei Wochen nach Ausstellungsdatum seine Gültigkeit.
            Anschließend muss eine neue Indikation angefordert werden.
            Diese Indikation stellt keine Finanzierungszusage dar und ersetzt weder eine Finanzierungsberatung, noch eine weitergehende Bonitätsprüfung und Immobilienbewertung.
            Sie begründet keinen Rechtsanspruch auf Abschluss eines Darlehensvertrages oder eine Darlehensauszahlung.
        </p>
        <p>
            Auf Basis der von deiner Kundin/deinem Kunden gemachten Angaben und Annahmen war die Finanzierungsvorprüfung durch dich als Berater:in unter Vorbehalt erfolgreich. Die monatliche Rate und die sich daraus ergebende finanzielle Belastung zu aktuell marktüblichen Zinsen würde bei einer beantragten Finanzierung - vorbehaltlich einer Prüfung von Detailnachweisen – eventuell positiv bewertet werden. Sofern sich nach einer Bonitätsprüfung und einer Objektbewertung der Bank oder aufgrund sonstiger die Finanzierung betreffender Informationen keine Ablehnungsgründe ergeben, könnte das Darlehen eventuell vergeben werden. Für eine Konkretisierung, bitte deine Kundin/deinen Kundin um Einreichung noch fehlender Informationen und Nachweise.
        </p>

        <h3><HomeFinancialFeasibilityIcon name="red" /> Rot</h3>
        <p>
            Eine rote Ampel ist eine Indikation für eine noch nicht erfolgreiche Vorprüfung der Kaufkraft deiner Kundin/ deines Kunden auf Basis der von ihr/ihm gemachten Angaben. Eine Finanzierung der Immobilie ist mit den aktuell vorliegenden Informationen voraussichtlich nicht möglich.
        </p>
        <p>
            Diese Indikation verliert zwei Wochen nach Ausstellungsdatum seine Gültigkeit. Anschließend muss eine neue Indikation angefordert werden.
            Diese Indikation stellt keine Finanzierungsabsage dar und ersetzt weder eine Finanzierungsberatung, noch eine weitergehende Bonitätsprüfung und Immobilienbewertung.
            Sie begründet vor allem keinen Rechtsanspruch auf Abschluss eines Darlehensvertrages oder eine Darlehensauszahlung.
        </p>
        <p>
            Auf Basis der von deiner Kundin/deinem Kunden gemachten Angaben und Annahmen war die Finanzierungsvorprüfung durch dich als Berater:in noch nicht erfolgreich.
            Die monatliche Rate und die sich daraus ergebende finanzielle Belastung zu aktuell marktüblichen Zinsen würde bei einer beantragten
            Finanzierung - vorbehaltlich einer Prüfung von Detailnachweisen – voraussichtlich nicht positiv bewertet werden. Sofern sich nach einer Bonitätsprüfung
            und einer Objektbewertung der Bank oder aufgrund sonstiger die Finanzierung betreffender Informationen konkrete Ablehnungsgründe ergeben, wird kein Darlehen vergeben.
            Solltest deine Kundin/dein Kunde dennoch einen Darlehensvorschlag zu diesem Objekt wünschen, bitte sie, dir weitere Informationen oder Nachweise einzureichen.
        </p>

    </div>
}

export default HomeFinancialFeasibility;
